/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
import {
  Mappings,
  SearchFilters,
  UniqueConditions
} from '@ion/features/src/search/types';
import {validateEntityMap} from './utils';

interface UseModularSearchResult {
  getSearchParams: (filters: SearchFilters) => void;
  mappings: BaseSearchMapping;
  filters: SearchFilters;
}

interface UseModularSearchProps {
  onRefresh?: () => void;
  onUpdate?: (filters: SearchFilters) => void;
  onReset?: () => void;
  globalGroupId: number;
  entityMappings: Mappings;
  uniqueConditions?: UniqueConditions;
  viewSubGroups?: boolean;
}

const getDefaultUniqueConditions = (): UniqueConditions => {
  const {_in, is, contains, between} = translate('common:modularsearch.attributes', {returnObjects: true});
  return (`(${contains}|${_in}|${is}|${between})`);
};

/**
 * Re-usable hook holding common functionality for using modular search in entity modules.
 */
export const useModularSearch = ({
  globalGroupId,
  onReset,
  onUpdate,
  onRefresh,
  entityMappings,
  uniqueConditions,
  viewSubGroups
}: UseModularSearchProps): UseModularSearchResult => {
  const [filters, setFilters] = useState<SearchFilters>();
  const _uniqueConditions     = useMemo(() => uniqueConditions || getDefaultUniqueConditions(), [uniqueConditions]);

  const getSearchParams = useCallback(function(newFilters: SearchFilters) {
    onUpdate?.(newFilters);
    if (!isObjectEmpty(newFilters)) {
      setFilters(newFilters);
    } else {
      setFilters(null);
    }
    onRefresh?.();
  }, []);

  const mappings = useMemo(() => {
    const account = {accountId: globalGroupId, viewSubGroups};
    return new BaseSearchMapping(validateEntityMap(entityMappings), _uniqueConditions, account);
  }, [entityMappings, _uniqueConditions, globalGroupId, viewSubGroups]);

  return {getSearchParams, mappings, filters};
};

export default useModularSearch;
